import './styles.css'

import { useDeliveryRequests } from '@api/useDeliveryRequests'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { ControlBarActionButtons } from '@components/OrderDispatch/OperationsRequests/ControlBarActionButtons'
import {
  GraviButton,
  GraviGrid,
  Horizontal,
  NotificationMessage,
  Texto,
  Vertical,
  useLocalStorage,
} from '@gravitate-js/excalibrr'

import type { SiteMetadata } from '@/api/useDeliveryRequests/response'
import { getRowId } from '@/components/_SiteWide/Grid/util'
import type { DayRange } from '@/hooks/useDateRangeFilter'
import { LockFilled } from '@ant-design/icons'
import { useUserSettings } from '@hooks/settings'
import { getCurrentMarket } from '@utils/general'
import type { RowNode } from 'ag-grid-community'
import { Tooltip } from 'antd'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RequestModal } from './components/modal/RequestModal'
import { getColumnDefs } from './util'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'delivery-requests'

export function OrderRequests() {
  const [visible, setVisible] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const gridAPIRef = useRef(null)
  const columnApiRef = useRef(null)
  const { updateSetting, getSetting } = useUserSettings()
  const { value: deliveryRequestDayRange, setValue: setDRDayRange } = useLocalStorage('deliveryRequest-dayRange', {
    back: -2,
    ahead: 0,
  })
  const { value: selectedStatus, setValue: setSelectedStatus } = useLocalStorage<string | null>(
    'deliveryRequest-selectedStatus'
  )
  const setStickyStatus = (value: string) => {
    setSelectedStatus(value)
    updateSetting('deliveryRequest-selectedStatus', value)
  }
  const setDeliveryRequestDayRange = (value: DayRange) => {
    setDRDayRange(value)
    updateSetting('deliveryRequest-dayRange', value)
  }

  const [filters, setFilters] = useState({
    from: moment()
      .add(deliveryRequestDayRange?.back ? deliveryRequestDayRange.back : -2, 'day')
      .startOf('day'),
    to: moment()
      .add(deliveryRequestDayRange?.ahead ? deliveryRequestDayRange.ahead : 0, 'day')
      .endOf('day'),
  })

  useEffect(() => {
    getSetting('deliveryRequest-selectedStatus').then((resp) => {
      const status = resp && resp !== 'null' ? resp : 'All'
      setSelectedStatus(status)
    })
  }, [])

  const { getDeliveryRequestsQuery, cancelRequestMutation } = useDeliveryRequests()
  const payload = useMemo(
    () => ({
      from_date: moment(filters.from).toISOString(true),
      to_date: moment(filters.to).toISOString(true),
      status: selectedStatus === 'All' ? null : selectedStatus,
    }),
    [filters, deliveryRequestDayRange, selectedStatus]
  )
  const { data, isFetching } = getDeliveryRequestsQuery(payload)
  const { scopes } = useSecurityContext()
  const editRequestScope = useMemo(() => scopes?.scopes?.includes('dr'), [scopes?.scopes])
  const market = getCurrentMarket()

  const handleCancel = async (id: string, note: string) => {
    try {
      await cancelRequestMutation.mutateAsync({ id, note })
      NotificationMessage('Canceled', 'The Request has been canceled successfully', false)
    } catch (error) {
      NotificationMessage('Not Canceled', 'The Request could not be canceled ', true)
    }
  }
  const siteOptions = useMemo(() => {
    if (market) {
      return data?.site_metadata?.filter((site: SiteMetadata) => site.market === market)
    }
    return data?.site_metadata
  }, [data?.site_metadata, market])

  const siteString = useMemo(() => {
    if (data?.credit_hold_sites?.length === 1) {
      return `Site ${data.credit_hold_sites[0]?.[0]} (${data.credit_hold_sites[0]?.[1]}) is on credit hold. Delivery Requests for this site cannot be fulfilled until this is removed.`
    }
    if (data?.credit_hold_sites?.length > 1) {
      return `There are ${data.credit_hold_sites.length} sites on credit hold. Delivery Requests for those sites cannot be fulfilled until this is removed.`
    }
    return ''
  }, [data?.credit_hold_sites])

  const getRowStyle = useCallback((params: RowNode) => {
    const diff = moment().diff(params?.data?.updated_date, 'seconds')
    if (diff < 3) {
      return { backgroundColor: 'var(--theme-color-2-dim)' }
    }
  }, [])

  const columnDefs = useMemo(
    () => getColumnDefs(handleCancel, setVisible, setSelectedRequest, editRequestScope),
    [gridAPIRef, editRequestScope, data]
  )

  const agPropsOverrides = useMemo(
    () => ({
      getRowId,
    }),
    [getRowId]
  )
  const onGridConfigChanged = useCallback(
    (config: Record<string, any>) => {
      updateSetting(`gridConfig::${STORAGE_KEY}`, config)
    },
    [updateSetting]
  )

  const showHoldBanner = useMemo(() => data?.credit_hold_sites?.length > 0, [data?.credit_hold_sites])

  const statusOptions = useMemo(
    () =>
      ['All', 'Received', 'Accepted', 'Dispatched', 'In Progress', 'Delivered', 'Canceled'].map((status) => ({
        label: status,
        value: status === 'Received' ? 'Pending' : status,
      })),
    []
  )
  return (
    <Vertical>
      {showHoldBanner && (
        <Horizontal verticalCenter horizontalCenter className={'p-2 '} style={{ background: 'var(--theme-error)' }}>
          <Texto appearance={'white'} category='label'>
            <LockFilled className={'mr-2'} />
            {siteString}
          </Texto>
          {data?.credit_hold_sites?.length > 1 && (
            <Tooltip
              defaultOpen
              color={'var(--bg-color-1)'}
              overlayInnerStyle={{
                opacity: 1,
                background: 'var(--bg-1)',
                padding: 10,
              }}
              overlayStyle={{ opacity: 1, backgroundColor: 'var(--bg-1)' }}
              placement={'bottom'}
              title={
                <Vertical style={{ minWidth: '250px' }}>
                  {data?.credit_hold_sites?.map((site) => {
                    return (
                      <Horizontal key={site[1]} className={'pb-1'} style={{ minWidth: '175px' }}>
                        <Vertical flex={1}>
                          <Texto style={{ fontSize: '1em' }}>{site[0]}</Texto>
                        </Vertical>
                        <Vertical flex={3}>
                          <Texto style={{ fontSize: '1em' }}>{site[1]}</Texto>
                        </Vertical>
                      </Horizontal>
                    )
                  })}
                </Vertical>
              }
            >
              <GraviButton
                className={'ghost-gravi-button'}
                style={{ color: 'white', fontWeight: 500 }}
                buttonText={'View Site List'}
              />
            </Tooltip>
          )}
        </Horizontal>
      )}
      <GraviGrid
        controlBarProps={{
          title: 'Order Requests',
          actionButtons: (
            <ControlBarActionButtons
              setVisible={setVisible}
              editScope={editRequestScope}
              filters={filters}
              setFilters={setFilters}
              selectedDayRange={deliveryRequestDayRange}
              setSelectedDayRange={setDeliveryRequestDayRange}
              selectedStatus={selectedStatus}
              setSelectedStatus={setStickyStatus}
              statusOptions={statusOptions}
            />
          ),
        }}
        columnDefs={columnDefs}
        agPropOverrides={agPropsOverrides}
        columnApiRef={columnApiRef}
        externalRef={gridAPIRef}
        storageKey={STORAGE_KEY}
        rowData={data?.rows}
        loading={isFetching}
        getRowStyle={getRowStyle}
        onGridConfigChanged={onGridConfigChanged}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
      <RequestModal
        setVisible={setVisible}
        visible={visible}
        selectedRequest={selectedRequest}
        setSelectedRequest={setSelectedRequest}
        siteMetadata={data?.site_metadata}
        siteOptions={siteOptions}
      />
    </Vertical>
  )
}
